import React, { useEffect, useState, useRef } from 'react';
import dynamic from 'next/dynamic';
import {
  Box,
  Container,
  Typography,
  TextField,
  Divider,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from '@mui/material';
import { ChevronRight, Delete } from '@mui/icons-material';
import { useRouter } from 'next/router';
import { fileToBase64 } from 'utils';
import { randomString } from 'utils/string';
import FileButton from 'components/FileButton';
import { useCustomServiceContext } from 'contexts/useCustomServiceContext';
import CustomServiceService from 'services/CustomServiceService';
import { useStyles } from './styles';
import ReactGA from 'react-ga4';
import CustomButton from 'components/CustomButton';
import { Autocomplete } from '@mui/material';
import { useAuthUser } from 'features/auth/AuthUserContext';
const ReCAPTCHA = dynamic(
  async () => {
    const { default: ReCAPTCHA } = await import('react-google-recaptcha');
    const reCaptcha = ({ forwardedRef, ...props }) => <ReCAPTCHA ref={forwardedRef} {...props} />;
    return reCaptcha;
  },
  { ssr: false },
);

const optionsScienceDesign = [
  'Infographics | Graphical Abstracts',
  'Presentation Design',
  'Cover Art',
  'Pharmaceutical Infographics',
  'Other',
];

export default function SpecificationsForm() {
  const classes = useStyles();
  const router = useRouter();
  const { user, profile } = useAuthUser();
  const recaptchaRef = useRef();
  const [isBusy, setIsBusy] = useState(false);
  const [errorFields, setErrorFields] = useState([]);
  const [customService, dispatchCustomService] = useCustomServiceContext();
  const [fileError, setFileError] = useState();
  const [formData, setFormData] = useState({
    userName: '',
    userEmail: '',
    file: null,
    typeScienceDesign: '',
    themeDescription: '',
  });

  function handleFieldChange(fieldName, value) {
    if (!!value && errorFields.includes(fieldName)) {
      setErrorFields(errorFields.filter((errorField) => errorField !== fieldName));
    }

    setFormData({ ...formData, [fieldName]: value });
  }

  async function handleFileChange(file, error) {
    setFileError();

    if (file) {
      setFormData({
        ...formData,
        file: {
          id: randomString(),
          name: file.name,
          content: (await fileToBase64(file)).content,
        },
      });
    } else if (error) {
      setFileError(error);
    }
  }

  function getErrorFields() {
    const fields = [];

    if (!formData.userName) {
      fields.push('userName');
    }

    if (!formData.userEmail) {
      fields.push('userEmail');
    }

    if (!formData.typeScienceDesign) {
      fields.push('typeScienceDesign');
    }

    if (!formData.themeDescription) {
      fields.push('themeDescription');
    }

    return fields;
  }

  function canSubmit() {
    const errors = getErrorFields();

    if (errors.length) {
      setErrorFields(errors);

      return false;
    }

    return true;
  }

  async function createCustomService() {
    await recaptchaRef.current.reset();

    const recaptchaToken = await recaptchaRef.current.executeAsync();
    const response = await CustomServiceService.create(
      {
        userName: formData.userName,
        userEmail: formData.userEmail,
        type: customService.type,
        quantity: customService.quantity,
        express: customService.express,
      },
      recaptchaToken,
    );

    return response.data;
  }

  async function createSpecification(newCustomService) {
    const response = await CustomServiceService.createSpecification(newCustomService.id, {
      name: 'no_name',
      size: 'custom',
      orientation: 'portrait',
      measurementUnit: 'pixels',
      width: 1,
      height: 1,
      dpi: 72,
      format: 'png',
      title: 'no_title',
      subtitle: 'no_subtitle',
      themeDescription: formData.themeDescription,
      contentDescription: 'no_content',
      journal: 'no_journal',
    });

    return response.data;
  }

  async function createAttachment(newCustomService, newSpecification) {
    if (formData.file) {
      return await CustomServiceService.createAttachment(newCustomService.id, {
        specId: newSpecification.id,
        reference: 'theme',
        content: formData.file.content,
      });
    }
  }

  async function handleFormSubmit(event) {
    event.preventDefault();

    if (!canSubmit()) {
      return;
    }

    try {
      setIsBusy(true);

      const newCustomService = await createCustomService();
      const newSpecification = await createSpecification(newCustomService);

      const attachmentResult = await createAttachment(newCustomService, newSpecification);
      const attachmentUrl = attachmentResult?.data?.url || '';

      dispatchCustomService({
        ...customService,
        id: newCustomService.id,
        userName: newCustomService.userName,
        userEmail: newCustomService.userEmail,
        specs: [newSpecification],
      });

      await CustomServiceService.requestCustomService({
        name: formData.userName,
        email: formData.userEmail,
        attachment: attachmentUrl,
        typeScienceDesign: formData.typeScienceDesign,
        description: formData.themeDescription,
      });

      ReactGA.event({
        category: 'custom_design',
        action: 'custom_service_form',
        label: 'When the user clicks on the "Send information for quote" button to submit the form',
        value: user.id,
      });

      router.push('/custom-services/thanks');
    } catch (e) {
      console.error(e);
    } finally {
      setIsBusy(false);
    }
  }

  useEffect(() => {
    if (user.email) {
      setFormData({ ...formData, userEmail: user.email });
    }

    if (profile.name) {
      setFormData({ ...formData, userName: profile.name });
    }
  }, [user, profile]);

  return (
    <>
      <ReCAPTCHA
        forwardedRef={recaptchaRef}
        size="invisible"
        hl="en"
        sitekey="6Ld938QaAAAAAC2SPQrFUEUeUCc78nACxVzyd8p0"
      />

      <Box py={6}>
        <Container>
          <Box mb={6}>
            <Typography
              id="header"
              className={classes.primaryColor}
              component="h1"
              variant="h6"
              gutterBottom={true}
            >
              <b>Custom Services</b>
            </Typography>
            <Typography>Let us know the initial project information.</Typography>
          </Box>

          <Box px={16} component="form" onSubmit={handleFormSubmit}>
            <Box display="flex" alignItems="center">
              <div className={classes.circle}>1</div>
              <Typography className={classes.title}>
                <b>Personal information</b>
              </Typography>
              <Divider className={classes.divider} />
            </Box>

            <div className={classes.personalInformation}>
              <Box mb={2}>
                <TextField
                  className={classes.input}
                  fullWidth={true}
                  variant="outlined"
                  error={errorFields.includes('userName')}
                  helperText={
                    errorFields.includes('userName')
                      ? 'Please fill this field correctly'
                      : undefined
                  }
                  label="Your name"
                  value={formData.userName}
                  onChange={(event) => handleFieldChange('userName', event.target.value)}
                />
              </Box>

              <Box mb={4}>
                <TextField
                  className={classes.input}
                  fullWidth={true}
                  variant="outlined"
                  error={errorFields.includes('userEmail')}
                  helperText={
                    errorFields.includes('userEmail')
                      ? 'Please fill this field correctly'
                      : undefined
                  }
                  label="Your e-mail"
                  value={formData.userEmail}
                  onChange={(event) => handleFieldChange('userEmail', event.target.value)}
                />
              </Box>
            </div>

            <Box display="flex" alignItems="center">
              <div className={classes.circle}>2</div>
              <Typography className={classes.title}>
                <b>Project Information</b>
              </Typography>
              <Divider className={classes.divider} />
            </Box>

            <Box pl={6} py={4}>
              <Box mb={4}>
                <Box display="flex" alignItems="flex-start" mb={2}>
                  <ChevronRight />
                  <Typography>
                    Attach a single file with everything that can help us understand your need.
                    Sketches are welcome.
                  </Typography>
                </Box>

                <Box pl={1}>
                  <FileButton
                    className={classes.fileButton}
                    variant="contained"
                    label="Attach data"
                    max={1}
                    extensions={['jpg', 'png', 'pdf', 'doc', 'docx']}
                    onChange={handleFileChange}
                  />

                  {fileError && (
                    <FormHelperText variant="outlined" error={true}>
                      {fileError}
                    </FormHelperText>
                  )}

                  {formData.file && (
                    <List className={classes.fileList} dense={true}>
                      <ListItem>
                        <ListItemText
                          className={classes.fileListText}
                          primary={formData.file.name}
                        />

                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => setFormData({ ...formData, file: null })}
                            size="large"
                          >
                            <Delete />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    </List>
                  )}
                </Box>
              </Box>

              <Box maxWidth={452} pb={4}>
                <Box display="flex" alignItems="flex-start" mb={2}>
                  <ChevronRight />
                  <Typography>What type of science design are you looking for?</Typography>
                </Box>

                <Autocomplete
                  options={optionsScienceDesign}
                  value={formData.organizationType}
                  onChange={(event, newValue) => handleFieldChange('typeScienceDesign', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Select science design"
                      name="typeScienceDesign"
                      error={errorFields.includes('typeScienceDesign')}
                      helperText={
                        errorFields.includes('typeScienceDesign')
                          ? 'Please fill this field correctly'
                          : undefined
                      }
                    />
                  )}
                />
              </Box>

              <Box display="flex" alignItems="flex-start" mb={2}>
                <ChevronRight />
                <Typography>
                  Write any preference, comment or detailed description of what the infographic
                  should look like <br />
                  and then our team will get in touch!
                </Typography>
              </Box>

              <Box>
                <TextField
                  className={classes.input}
                  fullWidth={true}
                  multiline={true}
                  variant="outlined"
                  inputProps={{ minRows: 6 }}
                  error={errorFields.includes('themeDescription')}
                  helperText={
                    errorFields.includes('themeDescription')
                      ? 'Please fill this field correctly'
                      : undefined
                  }
                  label="Give us more details here."
                  value={formData.themeDescription}
                  onChange={(event) => handleFieldChange('themeDescription', event.target.value)}
                />
              </Box>
            </Box>

            <Box textAlign="center">
              <CustomButton type="submit" disabled={isBusy}>
                Send information for quote
              </CustomButton>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
}
